import type { Payload } from '@atlaskit/rovo-triggers/types';

import type { OpenRovoPayload, SetRovoContextPayload } from '../../custom-ui/bridge/types';
import { getRovoProductFromExtensionType } from '../../custom-ui/bridge/utils/getRovoProductFromExtensionType';
import { getIsRovoEnabled } from '../../web-client/queries/getIsRovoEnabled';
import { getRovoAgentId, DEFAULT_AGENT_REFERENCE } from '../../web-client/queries/getRovoAgentId';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

// ─── constants ───────────────────────────────────────────────────────────────

const FORGE_SOURCE = 'forge-bridge-open-rovo';
const ROVO_EVENT_TYPE = 'chat-new';
const makeForgeAgentExternalConfigReference = (
	appId: string,
	installationId: string,
	environmentId: string,
	agentKey: string,
) =>
	`${installationId}_ari:cloud:ecosystem::extension/${appId}/${environmentId}/static/${agentKey}`;

// ─── openRovo ────────────────────────────────────────────────────────────────

type OpenRovoContext = MakeBridgeContext<
	'resolverContext' | 'extension' | 'extensionDetails',
	'relayEnvironment' | 'rovoPublisher'
>;

/**
 * Opens the Rovo chat panel by publishing an ai-mate trigger event.
 *
 * Mirrors the v1 behavior previously implemented in useRovoAgentsChat:
 * - Resolves the Rovo product from the extension type.
 * - Guards on cloudId and product availability.
 * - Resolves the agent reference via forge-agent external config reference,
 *   named-agent lookup (getRovoAgentId), or the default Rovo chat agent.
 */
export const openRovo: TBridgeMethod<OpenRovoContext, OpenRovoPayload, boolean> = async (
	{ resolverContext, extension, extensionDetails, services: { relayEnvironment, rovoPublisher } },
	{ agentKey, agentName, prompt },
) => {
	const { cloudId } = resolverContext;
	const product = getRovoProductFromExtensionType(extension.type);

	if (!cloudId || !product) {
		return false;
	}

	const appId = extensionDetails.appId;
	const installationId = extension.installationId;
	const environmentId = extension.environmentId;

	const publishEventDetails = {
		type: ROVO_EVENT_TYPE,
		product,
		source: FORGE_SOURCE,
	};

	let agentIdConfig: { agentExternalConfigReference: string } | { agentId: string };
	if (agentName && agentKey) {
		// Forge-managed agent: reference by external config reference ARI
		agentIdConfig = {
			agentExternalConfigReference: makeForgeAgentExternalConfigReference(
				appId,
				installationId,
				environmentId,
				agentKey,
			),
		};
	} else {
		// Named agent (by name only, no key) or default Rovo chat agent
		const agentId =
			agentName && !agentKey
				? await getRovoAgentId({
						agentAppId: appId,
						cloudId,
						input: { name: agentName },
						product,
						// agentKey is guaranteed falsy in this branch (condition: agentName && !agentKey)
						relayEnvironment,
					})
				: DEFAULT_AGENT_REFERENCE;
		agentIdConfig = { agentId };
	}

	rovoPublisher({
		...publishEventDetails,
		data: {
			dialogues: [],
			...agentIdConfig,
			...(prompt ? { prompt } : {}),
		},
	} as Payload);

	return true;
};

// ─── setRovoContext ───────────────────────────────────────────────────────────

type SetRovoContextBridgeContext = MakeBridgeContext<never, 'rovoPublisher'>;

/**
 * Sets or clears a typed Rovo context without opening chat.
 * The caller supplies a `*-context-payload` trigger; the host always overrides
 * `openChat` to prevent an app from opening the Rovo Chat UI.
 */
export const setRovoContext: TBridgeMethod<
	SetRovoContextBridgeContext,
	SetRovoContextPayload,
	boolean
> = async ({ services: { rovoPublisher } }, payload) => {
	rovoPublisher({
		type: payload.type,
		source: payload.source,
		product: payload.product,
		openChat: false,
		data: payload.data,
	} as Payload);

	return true;
};

// ─── isRovoEnabled ───────────────────────────────────────────────────────────

type IsRovoEnabledContext = MakeBridgeContext<'resolverContext', 'relayEnvironment'>;

/**
 * Returns whether Rovo is enabled for the current site.
 *
 * Returns `false` immediately when `cloudId` is absent on the resolver context
 * or when the Relay environment is unavailable.
 */
export const isRovoEnabled: TBridgeMethod<IsRovoEnabledContext, void, boolean> = async ({
	resolverContext,
	services: { relayEnvironment },
}) => {
	const { cloudId } = resolverContext;
	if (!cloudId) {
		return false;
	}
	return getIsRovoEnabled({ cloudId, relayEnvironment });
};
